import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, CircularProgress, Container, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import { db } from '@/firebase/firebase';
import { logger } from '@/services/loggerService';
import { RouteMap } from '@/routes/Routes';
import { toCampaignDoc, type CampaignDoc } from './types';

const CONTENT_HTML_ALLOWED_TAGS = [
  'div',
  'p',
  'img',
  'br',
  'strong',
  'em',
  'u',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'span',
];
const CONTENT_HTML_ALLOWED_ATTR = ['src', 'alt', 'href', 'target', 'rel', 'class'];

// `target` is allowed above, so every targeted link must be forced to
// noopener/noreferrer — otherwise stored content could carry rel="opener"
// (or rely on legacy browsers) and the opened page could navigate this tab
// via window.opener (reverse tabnabbing).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Detail page for a single What's New post.
 * Reads the `campaigns` doc by slug from Firestore directly (public read).
 * `contentHtml` is sanitized client-side before render — Firestore is public-write-adjacent
 * (any compromised or misconfigured write must not become stored XSS).
 */
const WhatsNewDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<CampaignDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    async function fetchPost() {
      try {
        const q = query(collection(db, 'campaigns'), where('active', '==', true), where('slug', '==', slug));
        const snapshot = await getDocs(q);
        if (cancelled) return;

        const doc = snapshot.docs[0];
        if (!doc) {
          navigate(RouteMap.WHATS_NEW, { replace: true });
          return;
        }

        const data = toCampaignDoc(doc.id, doc.data());
        if (!data.publishedAt) {
          navigate(RouteMap.WHATS_NEW, { replace: true });
          return;
        }

        setPost(data);
      } catch (err) {
        if (cancelled) return;
        logger.warn('firebase', "Failed to load What's New post", err);
        setError('Could not load this post. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  return (
    <>
      <Helmet>
        <title>{post ? `${post.title} — NoteGo` : "What's New — NoteGo"}</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Button
          component={RouterLink}
          to={RouteMap.WHATS_NEW}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 4 }}
          color="inherit"
        >
          What's New
        </Button>

        {loading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Typography color="error">{error}</Typography>}

        {!loading && !error && post && (
          <>
            {post.heroImageUrl && (
              <Box
                component="img"
                src={post.heroImageUrl}
                alt={post.title}
                sx={{ width: '100%', maxHeight: 400, objectFit: 'cover', borderRadius: 2, mb: 4 }}
              />
            )}
            <Typography variant="h4" fontWeight={700} mb={1.5}>
              {post.title}
            </Typography>
            {post.excerpt && (
              <Typography variant="body1" color="text.secondary" mb={1.5}>
                {post.excerpt}
              </Typography>
            )}
            {/* component="div" is required — MUI maps unlisted variants to an inline <span>,
                which flows alongside the inline-flex back button instead of onto its own line. */}
            <Typography variant="body2" component="div" color="text.secondary" mb={4}>
              {post.publishedAt
                ? new Date(post.publishedAt.toMillis()).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : ''}
            </Typography>
            {post.contentHtml && (
              <Box
                className="whats-new-content"
                sx={{
                  '& h1,& h2,& h3': { fontWeight: 700, mt: 3, mb: 1 },
                  '& p': { mb: 2, lineHeight: 1.8 },
                  '& ul,& ol': { pl: 3, mb: 2 },
                  '& img': { maxWidth: '100%', borderRadius: 1 },
                  '& a': { color: 'primary.main' },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(post.contentHtml, {
                    ALLOWED_TAGS: CONTENT_HTML_ALLOWED_TAGS,
                    ALLOWED_ATTR: CONTENT_HTML_ALLOWED_ATTR,
                  }),
                }}
              />
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default WhatsNewDetail;
